import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../components/Button";
import Input, { MaterialInputEvent } from "../../../components/v2/Input";
import Layout from "../../../components/v2/Layout";
import SignatureCard from "../../../components/v2/ValidarAssinatura/SignatureCard";
import DocumentoAssinadoService, {
  ResponseValidarDocumento,
} from "../../../services/DocumentoAssinadoService";
import { API_URL, isServerError } from "../../../config/api";
import Format from "../../../helpers/Format";

import style from "./styles.module.scss";

const keyI18n = "translations.validateSignature";

type Status = "idle" | "loading" | "notFound" | "serverError" | "success";

const statusIcons: { [key: string]: string } = {
  idle: "signature",
  notFound: "search_off",
  serverError: "database_off",
};

export default function ValidarAssinatura() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [fieldError, setFieldError] = useState("");
  const [document, setDocument] = useState<ResponseValidarDocumento>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmed = code.trim();

    if (!trimmed) {
      setFieldError(t(`${keyI18n}.field.required`));
      setStatus("idle");
      setDocument(undefined);
      return;
    }

    setFieldError("");
    setStatus("loading");
    setDocument(undefined);

    try {
      const response = await DocumentoAssinadoService.validar(trimmed);
      setDocument(response);
      setStatus("success");
    } catch (error) {
      setStatus(isServerError(error) ? "notFound" : "serverError");
    }
  };

  const message = statusIcons[status];

  return (
    <Layout title={t(`${keyI18n}.title`)}>
      <div className={style.body}>
        <form className={style.search} noValidate onSubmit={handleSubmit}>
          <header className={style.header}>
            <h1 className={`pgm-title-large ${style.title}`}>
              {t(`${keyI18n}.title`)}
            </h1>
            <p className={`pgm-body-large ${style.subtitle}`}>
              {t(`${keyI18n}.subtitle`)}
            </p>
          </header>

          <div className={style.inputRow}>
            <Input
              error={Boolean(fieldError)}
              errorText={fieldError}
              fullWidth
              label={t(`${keyI18n}.field.label`)}
              name="codigo"
              onInput={(event: MaterialInputEvent) => {
                setCode(event.target.value);

                if (fieldError) {
                  setFieldError("");
                }
              }}
              required
              value={code}
              variant="filled"
            />

            <Button
              className={style.submit}
              disabled={status === "loading"}
              icon={<span className="material-symbols-sharp">signature</span>}
              size="small"
              type="submit"
              variant="tonal"
            >
              {t(`${keyI18n}.submit`)}
            </Button>
          </div>
        </form>

        {message && (
          <div className={style.message}>
            <span aria-hidden="true" className={`material-symbols-sharp ${style.messageIcon}`}>
              {message}
            </span>
            <p className={`pgm-headline-small ${style.messageText}`}>
              {t(`${keyI18n}.messages.${status}`)}
            </p>
          </div>
        )}

        {status === "success" && document && (
          <>
            <section className={style.document}>
              <div className={style.documentHeader}>
                <div className={style.field}>
                  <span className={`pgm-title-medium ${style.fieldLabel}`}>
                    {t(`${keyI18n}.document.name`)}
                  </span>
                  <span className={`pgm-body-large ${style.fieldValue}`}>
                    {document.filename}
                  </span>
                </div>

                <Button
                  className={style.download}
                  href={`${API_URL}/documento_assinado/${document.control_code}/download`}
                  icon={<span className="material-symbols-sharp">download</span>}
                  size="small"
                  target="_blank"
                  variant="filled"
                >
                  {t(`${keyI18n}.document.download`)}
                </Button>
              </div>

              <div className={style.documentData}>
                <div className={style.field}>
                  <span className={`pgm-title-medium ${style.fieldLabel}`}>
                    {t(`${keyI18n}.document.code`)}
                  </span>
                  <span className={`pgm-body-large ${style.fieldValue}`}>
                    {document.control_code}
                  </span>
                </div>

                <div className={style.field}>
                  <span className={`pgm-title-medium ${style.fieldLabel}`}>
                    {t(`${keyI18n}.document.date`)}
                  </span>
                  <span className={`pgm-body-large ${style.fieldValue}`}>
                    {Format.date(document.date.toString(), "datetime")}
                  </span>
                </div>
              </div>
            </section>

            <section className={style.signatures}>
              <hr className={style.divider} />
              <h2 className={`pgm-title-large ${style.signaturesTitle}`}>
                {t(`${keyI18n}.signatures.title`)}
              </h2>

              <div className={style.signaturesGrid}>
                {document.signatures.map((signature, index) => (
                  <SignatureCard
                    date={signature.date}
                    key={`${signature.name}-${index}`}
                    name={signature.name}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}
